import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Play, Pause, ArrowCounterClockwise, SpeakerHigh, SpeakerSlash, CornersOut } from "@phosphor-icons/react";
import { useKidsSound } from "@/hooks/useKidsSound";
import { claimPlayback, releasePlayback } from "@/lib/lumiAudio";

// Kids Corner video player: original soundtrack, landscape (object-contain, never cropped), audible only after
// the visitor presses Play, follows the shared Sound toggle (muting keeps captions visible), never overlaps other
// sound (claimPlayback), and stops when unmounted. Controls are keyboard- and touch-friendly.
const CTRL = "w-10 h-10 rounded-full bg-black/55 hover:bg-black/75 text-white flex items-center justify-center shrink-0 focus-visible:outline focus-visible:outline-4 focus-visible:outline-[#b79cf0]";

export const KidsVideoPlayer = forwardRef(function KidsVideoPlayer({ src, poster, captions, title, playLabel, autoPlay = false, onEnded, onPlayingChange, testid = "kids-video", className = "" }, ref) {
  const wrapRef = useRef(null);
  const videoRef = useRef(null);
  const [soundOn] = useKidsSound();
  const [playing, setPlaying] = useState(false);
  const [started, setStarted] = useState(false);
  const [ended, setEnded] = useState(false);
  const [volume, setVolume] = useState(1);

  const stop = () => { const v = videoRef.current; if (v && !v.paused) v.pause(); };

  const play = async () => {
    const v = videoRef.current;
    if (!v) return;
    claimPlayback(stop);
    v.muted = !soundOn;
    v.volume = volume;
    try { await v.play(); } catch { /* needs a user gesture; controls stay available */ }
  };
  const pause = () => videoRef.current?.pause();
  const replay = () => { const v = videoRef.current; if (!v) return; v.currentTime = 0; setEnded(false); play(); };
  const fullscreen = () => {
    const el = wrapRef.current;
    const v = videoRef.current;
    if (el?.requestFullscreen) el.requestFullscreen().catch(() => {});
    else if (v?.webkitEnterFullscreen) v.webkitEnterFullscreen();
  };

  useEffect(() => { if (autoPlay) play(); return () => { stop(); releasePlayback(stop); }; }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { onPlayingChange?.(playing); }, [playing]); // eslint-disable-line react-hooks/exhaustive-deps
  useImperativeHandle(ref, () => ({ play, pause, replay, stop: () => { stop(); releasePlayback(stop); } }));
  useEffect(() => { if (videoRef.current) videoRef.current.muted = !soundOn; }, [soundOn]);
  useEffect(() => { if (videoRef.current) videoRef.current.volume = volume; }, [volume]);

  return (
    <div ref={wrapRef} className={`relative bg-black aspect-video w-full overflow-hidden ${className}`} data-testid={testid} data-playing={playing || undefined}>
      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-contain"
        poster={poster}
        preload="metadata"
        playsInline
        muted={!soundOn}
        onClick={() => (playing ? pause() : play())}
        onPlay={() => { setPlaying(true); setStarted(true); setEnded(false); }}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); setEnded(true); releasePlayback(stop); onEnded?.(); }}
        aria-label={title}
        data-testid={`${testid}-el`}
      >
        <source src={src} type="video/mp4" />
        {src.endsWith(".mp4") && <source src={src.replace(/\.mp4$/, ".webm")} type="video/webm" />}
        {captions && <track kind="captions" src={captions} srcLang="en" label="English" default />}
      </video>

      {!playing && (
        <button
          type="button"
          onClick={ended ? replay : play}
          className="absolute inset-0 flex items-center justify-center bg-black/10 hover:bg-black/20 transition-colors focus-visible:outline focus-visible:outline-4 focus-visible:outline-[#b79cf0]"
          aria-label={ended ? `Replay: ${title}` : `Play: ${title}`}
          data-testid={`${testid}-overlay-btn`}
        >
          <span className="w-20 h-20 rounded-full bg-white/90 border-4 border-[#6a3fc7] flex items-center justify-center shadow-lg">
            {ended ? <ArrowCounterClockwise size={34} weight="bold" className="text-[#6a3fc7]" /> : <Play size={34} weight="fill" className="text-[#6a3fc7] ml-1" />}
          </span>
        </button>
      )}

      {!started && (
        <p className="absolute left-4 bottom-4 right-28 font-nunito font-extrabold text-sm sm:text-base text-white drop-shadow-[0_2px_6px_rgba(0,0,0,0.8)] pointer-events-none" data-testid={`${testid}-caption`}>
          {playLabel || title}
        </p>
      )}

      <div className="absolute right-3 bottom-3 flex items-center gap-2" data-testid={`${testid}-controls`}>
        {started && (
          <>
            <button type="button" onClick={playing ? pause : play} aria-label={playing ? "Pause" : "Play"} className={CTRL} data-testid={`${testid}-toggle-btn`}>
              {playing ? <Pause size={20} weight="fill" /> : <Play size={20} weight="fill" />}
            </button>
            <button type="button" onClick={replay} aria-label="Replay from the start" className={CTRL} data-testid={`${testid}-replay-btn`}>
              <ArrowCounterClockwise size={20} weight="bold" />
            </button>
          </>
        )}
        <label className={`${CTRL} w-auto px-2 gap-1.5 cursor-pointer`} title={soundOn ? "Volume" : "Sound is off (Kids Corner Sound toggle)"}>
          {soundOn ? <SpeakerHigh size={18} weight="fill" /> : <SpeakerSlash size={18} weight="fill" />}
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={volume}
            disabled={!soundOn}
            onChange={(e) => setVolume(Number(e.target.value))}
            aria-label="Volume"
            className="w-16 h-1.5 accent-[#b79cf0] disabled:opacity-50"
            data-testid={`${testid}-volume`}
          />
        </label>
        <button type="button" onClick={fullscreen} aria-label="Fullscreen" className={CTRL} data-testid={`${testid}-fullscreen-btn`}>
          <CornersOut size={20} weight="bold" />
        </button>
      </div>
    </div>
  );
});
